import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { performantLogger } from '@/utils/performantLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary specifically designed for the chat system
 * Provides graceful fallback UI when chat components fail
 */
export class ChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    performantLogger.error('[ChatErrorBoundary] Component error caught:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Call optional onError callback
    this.props.onError?.(error, errorInfo);

    // Track error for analytics (if needed)
    if ((window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: `Chat Error: ${error.message}`,
        fatal: false
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default chat error UI
      return (
        <div className="flex items-center justify-center p-6 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-center max-w-md">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Chat Temporarily Unavailable
            </h3>
            <p className="text-red-700 mb-4">
              We're experiencing a technical issue with the chat system. 
              Please try refreshing or contact support if the problem persists.
            </p>
            <div className="space-y-2">
              <Button 
                onClick={this.handleRetry}
                variant="outline"
                size="sm"
                className="inline-flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
              {this.state.error && (
                <details className="text-xs text-red-600 mt-2">
                  <summary className="cursor-pointer">Technical Details</summary>
                  <pre className="mt-1 p-2 bg-red-100 rounded text-left overflow-auto">
                    {this.state.error.message}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
